import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from './EnterpriseAuthContext';
import { editorContextKey } from '../assistant/routeContext';
import { getTurn, isTurnActive, resetTurns, runningTurnBlockingSwitch, subscribeTurns } from '../assistant/assistantTurns';
import { appendChatBackup, clearChatBackups } from '../utils/tenantStorage';

const ChatMemoryContext = createContext();

/** Title of a conversation that has not been named yet. */
const DEFAULT_TITLE = 'New Chat';

/**
 * Whether a message row was removed (Retry / Regenerate dropped it). chat_messages has no
 * DELETE policy, so such rows are marked `assistant_state.removed` and every reader skips them
 * (the inky-agent edge function does the same).
 * @param {object|null|undefined} row - chat_messages row.
 * @returns {boolean} True when the row is removed.
 */
export const isRemovedMessage = (row) => row?.assistant_state?.removed === true;

/** Loose UUID shape check (account ids are interpolated into a PostgREST `or` filter). */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Limit a chat_conversations query to the active account. Conversations written before
 * account_id was recorded (account_id null) stay visible to their owner in any account.
 * @param {object} query - Supabase query builder on chat_conversations.
 * @param {string|null} accountId - Active account id.
 * @returns {object} Filtered query builder.
 */
export const scopeConversationQuery = (query, accountId) => {
  if (!accountId) return query.is('account_id', null);
  if (!UUID_PATTERN.test(String(accountId))) return query.eq('account_id', accountId);
  return query.or(`account_id.eq.${accountId},account_id.is.null`);
};

/**
 * Whether a conversation row may be shown in the active account (same account, or a legacy
 * row with no account).
 * @param {object|null|undefined} conversation - chat_conversations row.
 * @param {string|null} accountId - Active account id.
 * @returns {boolean} True when it belongs to the active account.
 */
export const isConversationInAccount = (conversation, accountId) => {
  if (!conversation) return false;
  if (!conversation.account_id) return true;
  return conversation.account_id === accountId;
};

/**
 * Hook to access chat memory context.
 * @returns {object} Chat memory context value.
 */
export const useChatMemory = () => {
  const context = useContext(ChatMemoryContext);
  if (!context) {
    throw new Error('useChatMemory must be used within ChatMemoryProvider');
  }
  return context;
};

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_CURRENT_CONVERSATION: 'SET_CURRENT_CONVERSATION',
  SET_MESSAGES: 'SET_MESSAGES',
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  SET_CONVERSATIONS: 'SET_CONVERSATIONS',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_AUTO_SAVE: 'SET_AUTO_SAVE',
};

// Initial state
const initialState = {
  currentConversation: null,
  messages: [],
  conversations: [],
  loading: false,
  error: null,
  autoSave: true,
};

// Reducer
const chatMemoryReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_CURRENT_CONVERSATION:
      return { ...state, currentConversation: action.payload };
    case ACTIONS.SET_MESSAGES:
      return { ...state, messages: action.payload };
    case ACTIONS.ADD_MESSAGE:
      return { ...state, messages: [...state.messages, action.payload] };
    case ACTIONS.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map((m) => (m.id === action.payload.id ? { ...m, ...action.payload } : m)),
      };
    case ACTIONS.SET_CONVERSATIONS:
      return { ...state, conversations: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    case ACTIONS.SET_AUTO_SAVE:
      return { ...state, autoSave: action.payload };
    default:
      return state;
  }
};

/**
 * ChatMemoryProvider component.
 *
 * Conversation identity is tracked in refs that are updated synchronously with every state
 * change, so `saveMessage` is a stable callback that always targets the conversation that is
 * current NOW (or the one the caller names) instead of the one captured when a handler was
 * created. That closure bug used to split the first exchange of a new chat across two
 * conversations.
 *
 * Document and template editors get ONE conversation each (context key `editor-doc:<id>` /
 * `editor-template:<id>`): opening an editor resumes that conversation, or starts an empty
 * thread that is created on the first message. The key is stored in each message's
 * `assistant_state.contextKey` (no schema change). Leaving the editor restores the general
 * conversation that was open before.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} Provider component.
 */
export const ChatMemoryProvider = ({ children }) => {
  const location = useLocation();
  const { state: authState } = useAuth();
  const user = authState.user;
  const accountId = authState.activeAccount?.id || null;
  /** @type {React.MutableRefObject<string|null>} Active account right now (for checks after an await). */
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;
  /** @type {React.MutableRefObject<{userId: string|null, accountId: string|null}>} Scope the loaded chat state belongs to. */
  const loadedScopeRef = useRef({ userId: null, accountId: null });
  const [state, dispatch] = useReducer(chatMemoryReducer, initialState);
  const createConversationPromiseRef = useRef(null);
  /** @type {React.MutableRefObject<Map<string, number>>} Last known max sequence_number per conversation. */
  const sequenceCursorRef = useRef(new Map());
  /** @type {React.MutableRefObject<object|null>} Conversation that is current right now. */
  const currentConversationRef = useRef(null);
  /** @type {React.MutableRefObject<Array<object>>} Messages of the current conversation right now. */
  const messagesRef = useRef([]);
  /** @type {React.MutableRefObject<Array<object>>} Latest conversation list. */
  const conversationsRef = useRef([]);
  /** @type {React.MutableRefObject<Map<string, string>>} conversation id -> editor context key. */
  const contextKeyByConversationRef = useRef(new Map());
  /** @type {React.MutableRefObject<Map<string, string>>} editor context key -> conversation id. */
  const conversationByContextRef = useRef(new Map());
  /** @type {React.MutableRefObject<string|null>} Context key the next new conversation belongs to. */
  const pendingContextKeyRef = useRef(null);
  /** @type {React.MutableRefObject<string|null>} General conversation to restore when leaving an editor. */
  const generalConversationIdRef = useRef(null);
  /**
   * @type {React.MutableRefObject<number>} Bumped on every conversation switch the route (or
   * sign-in restore) starts; an async switch applies its result only while its token is current.
   */
  const contextSwitchSeqRef = useRef(0);
  /** @type {React.MutableRefObject<Promise<void>|null>} Editor-context lookup in flight (saveMessage waits for it). */
  const contextLookupRef = useRef(null);

  /**
   * Make a conversation current (state + ref, synchronously).
   * @param {object|null} conversation - Conversation row.
   * @returns {void}
   */
  const setCurrent = useCallback((conversation) => {
    currentConversationRef.current = conversation;
    dispatch({ type: ACTIONS.SET_CURRENT_CONVERSATION, payload: conversation });
  }, []);

  /**
   * Replace the visible messages (state + ref, synchronously).
   * @param {Array<object>} messages - Messages.
   * @returns {void}
   */
  const setMessages = useCallback((messages) => {
    messagesRef.current = messages;
    dispatch({ type: ACTIONS.SET_MESSAGES, payload: messages });
  }, []);

  /**
   * Syncs the local sequence cursor from loaded messages (max sequence_number).
   * @param {string} conversationId - Conversation id.
   * @param {Array<{ sequence_number?: number }>} msgs - Messages for that conversation.
   * @returns {void}
   */
  const syncSequenceCursorFromMessages = useCallback((conversationId, msgs) => {
    if (!conversationId) return;
    if (!msgs?.length) {
      sequenceCursorRef.current.set(conversationId, 0);
      return;
    }
    const max = Math.max(...msgs.map((m) => Number(m.sequence_number) || 0));
    sequenceCursorRef.current.set(conversationId, max);
  }, []);

  /**
   * List conversations for the current user.
   * @param {object} options - Query options.
   * @param {boolean} options.includeArchived - Include archived conversations.
   * @param {number} options.limit - Maximum number of conversations to fetch.
   * @returns {Promise<Array>} List of conversations.
   */
  const listConversations = useCallback(async (options = {}) => {
    if (!user) return [];

    const { includeArchived = false, limit = 50 } = options;

    try {
      let query = scopeConversationQuery(
        supabase
          .from('chat_conversations')
          .select('*')
          .eq('user_id', user.id),
        accountId
      )
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (!includeArchived) {
        query = query.eq('is_archived', false);
      }

      const { data, error } = await query;

      if (error) throw error;

      // The user switched account while this ran: do not show the old account's list.
      if (accountIdRef.current !== accountId) return [];

      conversationsRef.current = data || [];
      dispatch({ type: ACTIONS.SET_CONVERSATIONS, payload: data || [] });
      return data || [];
    } catch (error) {
      console.error('Error listing conversations:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return [];
    }
  }, [user, accountId]);

  /**
   * Create a new conversation and make it current.
   * @param {string} [title] - Optional conversation title.
   * @param {string} [accountId] - Optional account ID.
   * @param {{contextKey?: string|null}} [options] - Editor context the conversation belongs to.
   * @returns {Promise<object>} Created conversation.
   */
  const createConversation = useCallback(async (title = DEFAULT_TITLE, accountId = null, options = {}) => {
    if (!user) throw new Error('User not authenticated');
    const contextKey = options.contextKey ?? null;

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });

      const { data, error } = await supabase
        .from('chat_conversations')
        .insert([{
          user_id: user.id,
          account_id: accountId || authState.activeAccount?.id,
          title: title || DEFAULT_TITLE,
        }])
        .select()
        .single();

      if (error) throw error;

      if (contextKey) {
        contextKeyByConversationRef.current.set(data.id, contextKey);
        conversationByContextRef.current.set(contextKey, data.id);
      }
      // A new conversation supersedes any route-driven switch still in flight.
      contextSwitchSeqRef.current += 1;
      pendingContextKeyRef.current = null;
      setCurrent(data);
      setMessages([]);
      syncSequenceCursorFromMessages(data.id, []);

      listConversations();

      return data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [user, authState.activeAccount, syncSequenceCursorFromMessages, listConversations, setCurrent, setMessages]);

  /**
   * Load a conversation and its messages.
   * @param {string} conversationId - Conversation ID to load.
   * @param {{isStale?: () => boolean}} [options] - `isStale` is checked after the fetch; when it
   *   returns true the result is discarded (a newer switch won) and null is returned.
   * @returns {Promise<object|null>} Loaded conversation (null when discarded as stale).
   */
  const loadConversation = useCallback(async (conversationId, options = {}) => {
    const isStale = typeof options.isStale === 'function' ? options.isStale : null;
    if (!user) throw new Error('User not authenticated');
    const requestedAccountId = accountIdRef.current;

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });

      const { data: conversation, error: convError } = await supabase
        .from('chat_conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (convError) throw convError;
      // The account changed during the fetch: drop the result quietly (a newer switch won).
      if (accountIdRef.current !== requestedAccountId) return null;
      if (!isConversationInAccount(conversation, requestedAccountId)) {
        throw new Error('That conversation belongs to another workspace.');
      }

      const { data: messages, error: msgError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('sequence_number', { ascending: true });

      if (msgError) throw msgError;

      const all = messages || [];
      const loaded = all.filter((m) => !isRemovedMessage(m));
      const taggedKey = all.find((m) => m.assistant_state?.contextKey)?.assistant_state?.contextKey;
      if (taggedKey) {
        contextKeyByConversationRef.current.set(conversationId, taggedKey);
      }
      if (isStale?.()) return null;
      // The account changed during the fetch: never show the old account's thread.
      if (!isConversationInAccount(conversation, accountIdRef.current)) return null;
      pendingContextKeyRef.current = null;
      setCurrent(conversation);
      setMessages(loaded);
      // Removed rows still hold their sequence numbers.
      syncSequenceCursorFromMessages(conversationId, all);
      return conversation;
    } catch (error) {
      console.error('Error loading conversation:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [user, syncSequenceCursorFromMessages, setCurrent, setMessages]);

  /**
   * Next sequence number for a conversation (local cursor; DB max when the cursor is unknown).
   * @param {string} conversationId - Conversation id.
   * @returns {Promise<number>} Next sequence number.
   */
  const nextSequenceNumber = useCallback(async (conversationId) => {
    let lastSeq = sequenceCursorRef.current.get(conversationId);
    if (lastSeq == null) {
      if (currentConversationRef.current?.id === conversationId) {
        lastSeq = messagesRef.current.length
          ? Math.max(...messagesRef.current.map((m) => Number(m.sequence_number) || 0))
          : 0;
      } else {
        const { data } = await supabase
          .from('chat_messages')
          .select('sequence_number')
          .eq('conversation_id', conversationId)
          .order('sequence_number', { ascending: false })
          .limit(1)
          .maybeSingle();
        lastSeq = Number(data?.sequence_number) || 0;
      }
    }
    const next = lastSeq + 1;
    sequenceCursorRef.current.set(conversationId, next);
    return next;
  }, []);

  /**
   * Save a message. Targets `options.conversationId` when given, else the conversation that
   * is current at call time; creates one (deduplicated) when there is none. The row is added
   * to the visible thread only when its conversation is still the current one.
   * @param {object} message - Message object with role and content.
   * @param {object} [assistantState] - Optional assistant state.
   * @param {object} [wizardUI] - Optional legacy wizard UI state.
   * @param {object} [toolCall] - Optional tool trace / result cards ({v: 2, steps, cards}).
   * @param {{conversationId?: string|null, title?: string}} [options] - Target conversation and title for a new one.
   * @returns {Promise<object>} Saved message row.
   */
  const saveMessage = useCallback(async (message, assistantState = null, wizardUI = null, toolCall = null, options = {}) => {
    if (!user) throw new Error('User not authenticated');

    if (!options.conversationId && !currentConversationRef.current && contextLookupRef.current) {
      // The route just switched editor context and its conversation is still being looked up:
      // wait, so the message lands in that conversation instead of a duplicate or the old one.
      await contextLookupRef.current.catch(() => {});
    }
    let conversationId = options.conversationId || currentConversationRef.current?.id || null;
    if (!conversationId) {
      if (!createConversationPromiseRef.current) {
        const contextKey = pendingContextKeyRef.current;
        createConversationPromiseRef.current = createConversation(options.title || DEFAULT_TITLE, null, { contextKey })
          .finally(() => { createConversationPromiseRef.current = null; });
      }
      const created = await createConversationPromiseRef.current;
      conversationId = created?.id;
    }
    if (!conversationId) throw new Error('No active conversation');

    const contextKey = contextKeyByConversationRef.current.get(conversationId);
    const state = contextKey ? { ...(assistantState || {}), contextKey } : assistantState;

    try {
      const nextSeq = await nextSequenceNumber(conversationId);
      const { data, error } = await supabase
        .from('chat_messages')
        .insert([{
          conversation_id: conversationId,
          role: message.role,
          content: message.content,
          sequence_number: nextSeq,
          assistant_state: state,
          wizard_ui: wizardUI,
          tool_call: toolCall,
        }])
        .select()
        .single();

      if (error) throw error;

      if (currentConversationRef.current?.id === conversationId) {
        messagesRef.current = [...messagesRef.current, data];
        dispatch({ type: ACTIONS.ADD_MESSAGE, payload: data });
      }

      return data;
    } catch (error) {
      console.error('Error saving message:', error);
      sequenceCursorRef.current.delete(conversationId);

      // Best-effort local copy of the unsaved message, keyed by user + account and wiped on
      // logout and account switch (utils/tenantStorage).
      appendChatBackup(
        { userId: user.id, accountId: accountIdRef.current, conversationId },
        { ...message, timestamp: Date.now(), assistantState: state, toolCall }
      );

      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, [user, createConversation, nextSequenceNumber]);

  /**
   * Update fields of a saved message (e.g. a result card's status inside `tool_call`).
   * @param {string} messageId - Message id.
   * @param {{tool_call?: object, content?: string}} fields - Fields to update.
   * @returns {Promise<object|null>} Updated row.
   */
  const updateMessage = useCallback(async (messageId, fields) => {
    if (!user || !messageId) return null;
    const allowed = {};
    if ('tool_call' in fields) allowed.tool_call = fields.tool_call;
    if ('content' in fields) allowed.content = fields.content;
    // Optimistic: the card reflects the user's action immediately.
    messagesRef.current = messagesRef.current.map((m) => (m.id === messageId ? { ...m, ...allowed } : m));
    dispatch({ type: ACTIONS.UPDATE_MESSAGE, payload: { id: messageId, ...allowed } });
    const { data, error } = await supabase
      .from('chat_messages')
      .update(allowed)
      .eq('id', messageId)
      .select()
      .maybeSingle();
    if (error) {
      console.error('Error updating message:', error);
      throw error;
    }
    return data;
  }, [user]);

  /**
   * Save messages in batch.
   * @param {Array<object>} messages - Array of messages to save.
   * @returns {Promise<void>} Completion promise.
   */
  const saveMessageBatch = useCallback(async (messages) => {
    const conversationId = currentConversationRef.current?.id;
    if (!user || !conversationId || !messages?.length) return;

    try {
      const { data: maxSeqData, error: maxSeqError } = await supabase
        .from('chat_messages')
        .select('sequence_number')
        .eq('conversation_id', conversationId)
        .order('sequence_number', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (maxSeqError) throw maxSeqError;

      const startSeq = maxSeqData ? maxSeqData.sequence_number + 1 : 1;

      const messagesToInsert = messages.map((msg, index) => ({
        conversation_id: conversationId,
        role: msg.role,
        content: msg.content,
        sequence_number: startSeq + index,
        assistant_state: msg.assistantState,
        wizard_ui: msg.wizardUI,
        tool_call: msg.toolCall,
      }));

      const { data, error } = await supabase
        .from('chat_messages')
        .insert(messagesToInsert)
        .select();

      if (error) throw error;

      sequenceCursorRef.current.set(conversationId, startSeq + messages.length - 1);
      if (currentConversationRef.current?.id === conversationId) {
        setMessages([...messagesRef.current, ...(data || [])]);
      }
    } catch (error) {
      console.error('Error saving message batch:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, [user, setMessages]);

  /**
   * Update conversation title.
   * @param {string} conversationId - Conversation ID.
   * @param {string} title - New title.
   * @returns {Promise<void>} Completion promise.
   */
  const updateConversationTitle = useCallback(async (conversationId, title) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('chat_conversations')
        .update({ title: title })
        .eq('id', conversationId);

      if (error) throw error;

      if (currentConversationRef.current?.id === conversationId) {
        setCurrent({ ...currentConversationRef.current, title });
      }

      await listConversations();
    } catch (error) {
      console.error('Error updating conversation title:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, [user, listConversations, setCurrent]);

  /**
   * Forget a conversation that is no longer available (archived/deleted).
   * @param {Set<string>} ids - Conversation ids.
   * @returns {void}
   */
  const forgetConversations = useCallback((ids) => {
    for (const id of ids) {
      const key = contextKeyByConversationRef.current.get(id);
      if (key && conversationByContextRef.current.get(key) === id) {
        conversationByContextRef.current.delete(key);
      }
      if (generalConversationIdRef.current === id) generalConversationIdRef.current = null;
    }
    if (currentConversationRef.current && ids.has(currentConversationRef.current.id)) {
      const key = contextKeyByConversationRef.current.get(currentConversationRef.current.id);
      // An editor's thread starts over (empty, created on the next message) in the same context.
      pendingContextKeyRef.current = key || null;
      setCurrent(null);
      setMessages([]);
    }
  }, [setCurrent, setMessages]);

  /**
   * Archive a conversation.
   * @param {string} conversationId - Conversation ID.
   * @returns {Promise<void>} Completion promise.
   */
  const archiveConversation = useCallback(async (conversationId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('chat_conversations')
        .update({ is_archived: true })
        .eq('id', conversationId);

      if (error) throw error;

      forgetConversations(new Set([conversationId]));
      await listConversations();
    } catch (error) {
      console.error('Error archiving conversation:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, [user, listConversations, forgetConversations]);

  /**
   * Delete a conversation permanently.
   * @param {string} conversationId - Conversation ID.
   * @returns {Promise<void>} Completion promise.
   */
  const deleteConversation = useCallback(async (conversationId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('id', conversationId);

      if (error) throw error;

      forgetConversations(new Set([conversationId]));
      await listConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, [user, listConversations, forgetConversations]);

  /**
   * Delete multiple conversations permanently.
   * @param {Array<string>} conversationIds - Array of conversation IDs to delete.
   * @returns {Promise<void>} Completion promise.
   */
  const deleteConversationsBulk = useCallback(async (conversationIds) => {
    if (!user) throw new Error('User not authenticated');
    if (!conversationIds?.length) return;

    try {
      const { error } = await supabase
        .from('chat_conversations')
        .delete()
        .in('id', conversationIds);

      if (error) throw error;

      forgetConversations(new Set(conversationIds));
      await listConversations();
    } catch (error) {
      console.error('Error bulk deleting conversations:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  }, [user, listConversations, forgetConversations]);

  /**
   * Get the last conversation for the user.
   * @returns {Promise<object|null>} Last conversation or null.
   */
  const getLastConversation = useCallback(async () => {
    if (!user) return null;

    try {
      const { data, error } = await scopeConversationQuery(
        supabase
          .from('chat_conversations')
          .select('*')
          .eq('user_id', user.id),
        accountId
      )
        .eq('is_archived', false)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error getting last conversation:', error);
      return null;
    }
  }, [user, accountId]);

  /**
   * Remove the last message from the current conversation (DB + local state). Used by Retry
   * and Regenerate.
   * @returns {Promise<boolean>} True if a message was removed.
   */
  const removeLastMessage = useCallback(async () => {
    const conversationId = currentConversationRef.current?.id;
    const last = messagesRef.current[messagesRef.current.length - 1];
    if (!user || !conversationId || !last?.id) return false;

    try {
      const { data: deleted, error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('id', last.id)
        .select('id');

      if (error) throw error;

      const remaining = messagesRef.current.slice(0, -1);
      if (Array.isArray(deleted) && deleted.length) {
        setMessages(remaining);
        syncSequenceCursorFromMessages(conversationId, remaining);
        return true;
      }

      // chat_messages has no DELETE policy, so the delete matched nothing: mark the row
      // removed instead (every reader, including Inky's server agent, skips such rows). It
      // keeps its sequence number, so the cursor stays where it is.
      const state = last.assistant_state && typeof last.assistant_state === 'object' ? last.assistant_state : {};
      const { data: marked, error: markError } = await supabase
        .from('chat_messages')
        .update({ assistant_state: { ...state, removed: true, removedAt: new Date().toISOString() } })
        .eq('id', last.id)
        .select('id');
      if (markError) throw markError;
      if (!Array.isArray(marked) || !marked.length) throw new Error('That message could not be removed.');

      setMessages(remaining);
      const cursor = sequenceCursorRef.current.get(conversationId) || 0;
      sequenceCursorRef.current.set(conversationId, Math.max(cursor, Number(last.sequence_number) || 0));
      return true;
    } catch (err) {
      console.error('Error removing last message:', err);
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      return false;
    }
  }, [user, setMessages, syncSequenceCursorFromMessages]);

  /**
   * Leave the current conversation without deleting it. The next message starts a new one
   * (in the current editor context, if any).
   * @returns {void}
   */
  const clearCurrentConversation = useCallback(() => {
    contextSwitchSeqRef.current += 1;
    pendingContextKeyRef.current = editorContextKey(window.location.pathname, window.location.search);
    setCurrent(null);
    setMessages([]);
  }, [setCurrent, setMessages]);

  /**
   * Messages of a conversation as of now (the current thread only; others are not cached).
   * @param {string} conversationId - Conversation id.
   * @returns {Array<object>} Messages.
   */
  const getMessages = useCallback((conversationId) => (
    currentConversationRef.current?.id === conversationId ? messagesRef.current : []
  ), []);

  /**
   * Title of a conversation as of now.
   * @param {string} conversationId - Conversation id.
   * @returns {string|null} Title.
   */
  const getConversationTitle = useCallback((conversationId) => {
    if (currentConversationRef.current?.id === conversationId) return currentConversationRef.current.title || null;
    return conversationsRef.current.find((c) => c.id === conversationId)?.title || null;
  }, []);

  /**
   * Find the latest live conversation tagged with an editor context key. Looks among the
   * user's most recently updated live conversations first (so long archived threads for the
   * same document can never crowd out the live one), then falls back to any tagged message.
   * @param {string} contextKey - Editor context key.
   * @returns {Promise<string|null>} Conversation id.
   */
  const findContextConversation = useCallback(async (contextKey) => {
    const cached = conversationByContextRef.current.get(contextKey);
    if (cached) return cached;
    const remember = (id) => {
      if (id) {
        conversationByContextRef.current.set(contextKey, id);
        contextKeyByConversationRef.current.set(id, contextKey);
      }
      return id || null;
    };
    try {
      const { data: recent, error: recentError } = await scopeConversationQuery(
        supabase
          .from('chat_conversations')
          .select('id')
          .eq('user_id', user.id),
        accountId
      )
        .eq('is_archived', false)
        .order('updated_at', { ascending: false })
        .limit(50);
      if (recentError) throw recentError;
      const recentIds = (recent || []).map((row) => row.id);
      if (recentIds.length) {
        const { data: rows, error } = await supabase
          .from('chat_messages')
          .select('conversation_id')
          .eq('assistant_state->>contextKey', contextKey)
          .in('conversation_id', recentIds)
          .limit(200);
        if (error) throw error;
        const tagged = new Set((rows || []).map((row) => row.conversation_id));
        const id = recentIds.find((candidate) => tagged.has(candidate));
        if (id) return remember(id);
      }

      // Older than the 50 most recent conversations: find any tagged message, keep live ones.
      const { data: rows, error } = await supabase
        .from('chat_messages')
        .select('conversation_id')
        .eq('assistant_state->>contextKey', contextKey)
        .limit(500);
      if (error) throw error;
      const ids = [...new Set((rows || []).map((row) => row.conversation_id).filter(Boolean))].filter(
        (id) => !recentIds.includes(id)
      );
      if (!ids.length) return null;
      const { data: conversations, error: convError } = await scopeConversationQuery(
        supabase
          .from('chat_conversations')
          .select('id')
          .in('id', ids)
          .eq('user_id', user.id),
        accountId
      )
        .eq('is_archived', false)
        .order('updated_at', { ascending: false })
        .limit(1);
      if (convError) throw convError;
      return remember(conversations?.[0]?.id || null);
    } catch (error) {
      console.warn('Could not look up the editor conversation:', error);
      return null;
    }
  }, [user, accountId]);

  /**
   * The most recent live conversation that does NOT belong to a document or template editor
   * (no message carries `assistant_state.contextKey`), for restoring on a non-editor route.
   * @returns {Promise<object|null>} Conversation row or null.
   */
  const findGeneralConversation = useCallback(async () => {
    if (!user) return null;
    try {
      const { data: recent, error } = await scopeConversationQuery(
        supabase
          .from('chat_conversations')
          .select('*')
          .eq('user_id', user.id),
        accountId
      )
        .eq('is_archived', false)
        .order('updated_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      for (const conversation of recent || []) {
        if (contextKeyByConversationRef.current.has(conversation.id)) continue;
        const { data: tagged, error: tagError } = await supabase
          .from('chat_messages')
          .select('id, assistant_state')
          .eq('conversation_id', conversation.id)
          .not('assistant_state->>contextKey', 'is', null)
          .limit(1);
        if (tagError) throw tagError;
        const key = tagged?.[0]?.assistant_state?.contextKey;
        if (key) {
          contextKeyByConversationRef.current.set(conversation.id, key);
          continue;
        }
        return conversation;
      }
      return null;
    } catch (error) {
      console.error('Error finding the last general conversation:', error);
      return null;
    }
  }, [user, accountId]);

  /** Editor context of the current route (`editor-doc:<id>` / `editor-template:<id>`), or null. */
  const activeContextKey = editorContextKey(location.pathname, location.search);
  const activeContextKeyRef = useRef(activeContextKey);
  activeContextKeyRef.current = activeContextKey;
  /** @type {React.MutableRefObject<string|null|undefined>} Context key the current conversation was chosen for. */
  const appliedContextKeyRef = useRef(undefined);
  /** @type {React.MutableRefObject<{key: string, unsubscribe: () => void}|null>} Switch waiting for a running turn. */
  const deferredSwitchRef = useRef(null);
  /** @type {React.MutableRefObject<() => void>} Latest reconcileContext (called from the turn subscription). */
  const reconcileContextRef = useRef(() => {});

  /**
   * Make the current conversation match the route's editor context: editor routes resume
   * their own conversation (or an empty thread created on the first message); leaving an
   * editor restores the general conversation that was open before.
   *
   * A running turn keeps its conversation on screen: the switch waits until the turn settles.
   * When the turn itself navigated (Inky opened a page via app.navigate), the conversation
   * follows the user to the new page instead of being swapped out under the reply.
   * @returns {void}
   */
  const reconcileContext = useCallback(() => {
    if (!user) return;
    const target = activeContextKeyRef.current;
    const previous = appliedContextKeyRef.current;
    if (previous === target) return;
    const current = currentConversationRef.current;

    const blocking = runningTurnBlockingSwitch(current?.id || null);
    if (blocking) {
      if (deferredSwitchRef.current?.key === blocking.key) return;
      deferredSwitchRef.current?.unsubscribe();
      const unsubscribe = subscribeTurns(() => {
        const turn = getTurn(blocking.key);
        if (isTurnActive(turn)) return;
        unsubscribe();
        deferredSwitchRef.current = null;
        if (turn?.navigated) {
          // Inky moved the user here mid-conversation: keep talking in the same thread.
          appliedContextKeyRef.current = activeContextKeyRef.current;
          return;
        }
        reconcileContextRef.current();
      });
      deferredSwitchRef.current = { key: blocking.key, unsubscribe };
      return;
    }

    appliedContextKeyRef.current = target;
    // Nothing to do for a non-editor route that never had an editor context (first render).
    if (!target && !previous) return;
    contextSwitchSeqRef.current += 1;
    const token = contextSwitchSeqRef.current;
    const isStale = () => contextSwitchSeqRef.current !== token;
    const currentIsContext = current ? contextKeyByConversationRef.current.has(current.id) : false;

    /**
     * Track an async switch so saveMessage can wait for it.
     * @param {Promise<void>} promise - Switch work.
     * @returns {void}
     */
    const track = (promise) => {
      contextLookupRef.current = promise;
      promise.finally(() => {
        if (contextLookupRef.current === promise) contextLookupRef.current = null;
      });
    };

    if (target) {
      if (current && !currentIsContext) generalConversationIdRef.current = current.id;
      if (current && conversationByContextRef.current.get(target) === current.id) return;
      // Detach from the previous thread right away: a message sent while the lookup runs must
      // not land in it (saveMessage waits for the lookup instead).
      pendingContextKeyRef.current = target;
      setCurrent(null);
      setMessages([]);
      track((async () => {
        const id = await findContextConversation(target);
        if (isStale() || !id) return;
        try {
          await loadConversation(id, { isStale });
        } catch {
          if (isStale()) return;
          conversationByContextRef.current.delete(target);
          pendingContextKeyRef.current = target;
        }
      })());
      return;
    }

    pendingContextKeyRef.current = null;
    if (current && !currentIsContext) return;
    setCurrent(null);
    setMessages([]);
    const generalId = generalConversationIdRef.current;
    if (!generalId) return;
    track((async () => {
      try {
        await loadConversation(generalId, { isStale });
      } catch {
        // Logged in loadConversation; the panel stays on an empty general thread.
      }
    })());
  }, [user, findContextConversation, loadConversation, setCurrent, setMessages]);
  reconcileContextRef.current = reconcileContext;

  /**
   * Drop every piece of in-memory chat state (logout, or a switch to another user or account).
   * @returns {void}
   */
  const resetChatState = useCallback(() => {
    currentConversationRef.current = null;
    messagesRef.current = [];
    conversationsRef.current = [];
    sequenceCursorRef.current.clear();
    contextKeyByConversationRef.current.clear();
    conversationByContextRef.current.clear();
    pendingContextKeyRef.current = null;
    generalConversationIdRef.current = null;
    appliedContextKeyRef.current = undefined;
    contextLookupRef.current = null;
    createConversationPromiseRef.current = null;
    contextSwitchSeqRef.current += 1;
    deferredSwitchRef.current?.unsubscribe();
    deferredSwitchRef.current = null;
    resetTurns();
    dispatch({ type: ACTIONS.SET_CURRENT_CONVERSATION, payload: null });
    dispatch({ type: ACTIONS.SET_MESSAGES, payload: [] });
    dispatch({ type: ACTIONS.SET_CONVERSATIONS, payload: [] });
    dispatch({ type: ACTIONS.CLEAR_ERROR });
  }, []);

  // Sign-in / account switch: list conversations; on non-editor routes restore the last
  // GENERAL conversation. Logout or a switch to another user or account first drops the
  // previous scope's chat state (memory and browser backup), so tenant A's thread, document
  // snapshots and tool results never stay on screen in tenant B.
  useEffect(() => {
    if (user) {
      const previous = loadedScopeRef.current;
      const switched = previous.userId !== null
        && (previous.userId !== user.id || (previous.accountId !== null && previous.accountId !== accountId));
      loadedScopeRef.current = { userId: user.id, accountId };
      if (switched) {
        resetChatState();
        clearChatBackups();
      }
      listConversations();
      if (editorContextKey(window.location.pathname, window.location.search)) {
        return undefined;
      }
      const token = contextSwitchSeqRef.current;
      const isStale = () => contextSwitchSeqRef.current !== token || Boolean(currentConversationRef.current);
      (async () => {
        const general = await findGeneralConversation();
        if (!general || isStale()) return;
        generalConversationIdRef.current = general.id;
        try {
          await loadConversation(general.id, { isStale });
        } catch {
          // Logged in loadConversation; the panel starts empty.
        }
      })();
      return undefined;
    }
    // Signed out after being signed in (not the first render while the session loads):
    // drop the backups too. The auth logout paths also clear them.
    if (loadedScopeRef.current.userId !== null) clearChatBackups();
    loadedScopeRef.current = { userId: null, accountId: null };
    resetChatState();
    return undefined;
  }, [user, accountId, findGeneralConversation, loadConversation, listConversations, resetChatState]);

  // Route changes reconcile the current conversation with the editor context.
  useEffect(() => {
    reconcileContext();
  }, [activeContextKey, reconcileContext]);

  /**
   * Where a server-agent turn should go: the current conversation (waiting for an editor
   * lookup in flight, as saveMessage does), or a new one in the pending editor context.
   * @returns {Promise<{conversationId: string|null, contextKey: string|null}>} Target.
   */
  const resolveTurnTarget = useCallback(async () => {
    if (!currentConversationRef.current && contextLookupRef.current) {
      await contextLookupRef.current.catch(() => {});
    }
    const current = currentConversationRef.current;
    if (current) {
      return { conversationId: current.id, contextKey: contextKeyByConversationRef.current.get(current.id) || null };
    }
    return { conversationId: null, contextKey: pendingContextKeyRef.current || null };
  }, []);

  /**
   * Adopt a conversation the server agent created for the first message of a new chat. It
   * becomes current only while the new-chat thread is still on screen (the user may have moved).
   * @param {object} conversation - chat_conversations row.
   * @param {string|null} [contextKey] - Editor context the conversation belongs to.
   * @returns {void}
   */
  const adoptServerConversation = useCallback((conversation, contextKey = null) => {
    if (!conversation?.id) return;
    // A turn started in another account finished after a switch: never adopt its thread here.
    if (!isConversationInAccount(conversation, accountIdRef.current)) return;
    if (contextKey) {
      contextKeyByConversationRef.current.set(conversation.id, contextKey);
      conversationByContextRef.current.set(contextKey, conversation.id);
    }
    if (!currentConversationRef.current) {
      contextSwitchSeqRef.current += 1;
      pendingContextKeyRef.current = null;
      setCurrent(conversation);
      setMessages([]);
      syncSequenceCursorFromMessages(conversation.id, []);
    }
    listConversations();
  }, [listConversations, setCurrent, setMessages, syncSequenceCursorFromMessages]);

  /**
   * Fold a row the server agent saved into the visible thread (deduplicated, in sequence order)
   * and move the sequence cursor past it, so a later browser-side save never reuses its number.
   * @param {object} row - chat_messages row.
   * @returns {void}
   */
  const ingestServerMessage = useCallback((row) => {
    if (!row?.id || !row.conversation_id) return;
    const key = row.assistant_state?.contextKey;
    if (key && !contextKeyByConversationRef.current.has(row.conversation_id)) {
      contextKeyByConversationRef.current.set(row.conversation_id, key);
    }
    const seq = Number(row.sequence_number) || 0;
    const cursor = sequenceCursorRef.current.get(row.conversation_id);
    if (cursor == null || seq > cursor) sequenceCursorRef.current.set(row.conversation_id, seq);
    if (currentConversationRef.current?.id !== row.conversation_id) return;
    if (isRemovedMessage(row)) {
      if (messagesRef.current.some((m) => m.id === row.id)) setMessages(messagesRef.current.filter((m) => m.id !== row.id));
      return;
    }
    if (messagesRef.current.some((m) => m.id === row.id)) {
      messagesRef.current = messagesRef.current.map((m) => (m.id === row.id ? { ...m, ...row } : m));
      dispatch({ type: ACTIONS.UPDATE_MESSAGE, payload: row });
      return;
    }
    const next = [...messagesRef.current, row].sort((a, b) => (Number(a.sequence_number) || 0) - (Number(b.sequence_number) || 0));
    setMessages(next);
  }, [setMessages]);

  /**
   * Rows of a conversation after a sequence number (detached-turn polling).
   * @param {string} conversationId - Conversation id.
   * @param {number} afterSequence - Exclusive lower bound.
   * @returns {Promise<Array<object>>} Rows, oldest first.
   */
  const fetchMessagesAfter = useCallback(async (conversationId, afterSequence) => {
    if (!user || !conversationId) return [];
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .gt('sequence_number', Number(afterSequence) || 0)
      .order('sequence_number', { ascending: true });
    if (error) throw error;
    return (data || []).filter((row) => !isRemovedMessage(row));
  }, [user]);

  /**
   * Open a conversation the user picked (History). Supersedes any route-driven switch still
   * in flight, so a late editor lookup cannot replace the user's choice.
   * @param {string} conversationId - Conversation id.
   * @returns {Promise<object|null>} Loaded conversation.
   */
  const openConversation = useCallback((conversationId) => {
    contextSwitchSeqRef.current += 1;
    return loadConversation(conversationId);
  }, [loadConversation]);

  // Drop a pending deferred switch on unmount.
  useEffect(() => () => {
    deferredSwitchRef.current?.unsubscribe();
    deferredSwitchRef.current = null;
  }, []);

  const value = {
    // State
    currentConversation: state.currentConversation,
    messages: state.messages,
    conversations: state.conversations,
    loading: state.loading,
    error: state.error,
    autoSave: state.autoSave,
    activeContextKey,

    // Actions
    createConversation,
    loadConversation: openConversation,
    saveMessage,
    saveMessageBatch,
    updateMessage,
    updateConversationTitle,
    archiveConversation,
    deleteConversation,
    deleteConversationsBulk,
    listConversations,
    getLastConversation,
    clearCurrentConversation,
    removeLastMessage,

    // Live accessors (for work that outlives a render, e.g. Inky's agent loop)
    getMessages,
    getConversationTitle,

    // Server agent (inky-agent) turns
    resolveTurnTarget,
    adoptServerConversation,
    ingestServerMessage,
    fetchMessagesAfter,

    // Utility
    setAutoSave: (enabled) => dispatch({ type: ACTIONS.SET_AUTO_SAVE, payload: enabled }),
    clearError: () => dispatch({ type: ACTIONS.CLEAR_ERROR }),
  };

  return (
    <ChatMemoryContext.Provider value={value}>
      {children}
    </ChatMemoryContext.Provider>
  );
};
